import React, { useEffect, useRef, useState } from "react";
import { Pill } from "./Reveal";
import ShaderBackground from "./ShaderBackground";

// A working day with the tool, told by the clock. Each scene names what the
// tool did and what stayed with a person.
const SCENES = [
  {
    time: "07:50",
    state: "Read",
    head: "The week's list writes itself.",
    text: "The tool has read the tracker, the inbox and the calendar. One list: what is due, what is missing, and who owes what.",
  },
  {
    time: "09:15",
    state: "Prepared",
    head: "A request arrives with its history.",
    text: "The client's record, the last three messages and a drafted next step sit beside it. Nobody goes looking.",
  },
  {
    time: "11:00",
    state: "Approved",
    head: "Three reminders are drafted. None are sent.",
    text: "You read them, change one, and approve. They go out under your name.",
  },
  {
    time: "14:30",
    state: "Prepared",
    head: "The report is assembled. The reviewer reviews.",
    text: "The numbers come from your systems, with their sources attached. The partner checks the judgement, not the arithmetic.",
  },
  {
    time: "16:45",
    state: "Logged",
    head: "Everything it did is in the log.",
    text: "What it read, what it prepared, and who approved it. In your account.",
  },
];

// The scenes use the first part of the scroll; the outro then rises as the last slide.
const SCENES_END = 0.82;
const OUTRO_START = 0.86;

export default function Morning({ outro = null }) {
  const wrap = useRef(null);
  const slide = useRef(null);
  const [p, setP] = useState(0);
  const hasOutro = Boolean(outro);
  useEffect(() => {
    let raf = 0;
    const calc = () => {
      raf = 0;
      const el = wrap.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      const raw = Math.min(1, Math.max(0, -r.top / total));
      const pr = hasOutro ? Math.min(1, raw / SCENES_END) : raw;
      if (slide.current) {
        const s = Math.min(
          1,
          Math.max(0, (raw - OUTRO_START) / (1 - OUTRO_START)),
        );
        const eased = 1 - Math.pow(1 - s, 3);
        slide.current.style.transform = `translate3d(0, ${((1 - eased) * 104).toFixed(2)}%, 0)`;
      }
      setP((old) => (Math.abs(old - pr) > 0.001 ? pr : old));
    };
    const on = () => {
      if (!raf) raf = requestAnimationFrame(calc);
    };
    calc();
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => {
      window.removeEventListener("scroll", on);
      window.removeEventListener("resize", on);
      cancelAnimationFrame(raf);
    };
  }, [hasOutro]);

  const idx = Math.min(SCENES.length - 1, Math.floor(p * SCENES.length));
  const scene = SCENES[idx];
  return (
    <section
      id="monday"
      ref={wrap}
      className={`relative ${hasOutro ? "h-[640vh]" : "h-[520vh]"}`}
      data-testid="monday"
    >
      <div className="sticky top-0 h-[100svh] overflow-hidden bg-[#f7f7f7]">
        <ShaderBackground strength={0.36} lines={15} fadeLeft={0} />
        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col px-5 pb-8 pt-24">
          <div className="flex items-center justify-between">
            <Pill white>A Monday, with the tool</Pill>
            <span
              className="rounded-full bg-white px-4 py-1.5 text-[12px] font-medium tabular-nums"
              data-testid="monday-counter"
            >
              0{idx + 1} / 0{SCENES.length}
            </span>
          </div>

          <div className="grid flex-1 items-center gap-6 lg:grid-cols-12 lg:gap-10">
            <div
              className="nm-method-type min-w-0 lg:col-span-7"
              aria-live="polite"
            >
              <p
                className="nm-display whitespace-nowrap font-medium tabular-nums leading-[0.9] tracking-[-0.04em]"
                style={{ fontSize: "29cqw" }}
              >
                <span className="nm-word-mask">
                  <span key={scene.time} className="nm-word">
                    {scene.time}
                  </span>
                </span>
              </p>
            </div>
            <div className="min-w-0 lg:col-span-5">
              <div
                key={scene.time}
                className="animate-in fade-in slide-in-from-bottom-4 rounded-[24px] bg-white/85 p-5 backdrop-blur-md duration-700 sm:p-7"
              >
                <span className="inline-flex rounded-full bg-black px-3 py-1 text-[11px] font-medium text-white">
                  {scene.state}
                </span>
                <h3 className="nm-title mt-4 text-[28px] leading-[1.08] sm:text-[36px] xl:text-[40px]">
                  {scene.head}
                </h3>
                <p className="mt-3 max-w-[480px] text-[15px] leading-[1.4] opacity-70 sm:text-[17px]">
                  {scene.text}
                </p>
              </div>
            </div>
          </div>

          <div
            className="relative mt-6 h-[52px] w-full shrink-0"
            aria-hidden="true"
          >
            <span className="absolute left-0 right-0 top-[6px] h-px bg-black/15" />
            <span
              className="absolute left-0 top-[5px] h-[3px] rounded-full bg-black"
              style={{ width: `${Math.min(1, p) * 100}%` }}
            />
            {SCENES.map((s, k) => (
              <span
                key={s.time}
                className="absolute top-0 -translate-x-1/2"
                style={{ left: `${(k / (SCENES.length - 1)) * 100}%` }}
              >
                <span
                  className={`block h-3 w-3 rounded-full border border-black transition-colors duration-500 ${k <= idx ? "bg-black" : "bg-[#f7f7f7]"}`}
                />
                <span
                  className={`absolute top-5 whitespace-nowrap text-[11px] font-medium tabular-nums transition-opacity duration-500 ${k === idx ? "opacity-100" : "opacity-35"} ${k === 0 ? "left-0" : k === SCENES.length - 1 ? "right-0" : "left-1/2 -translate-x-1/2"}`}
                >
                  {s.time}
                </span>
              </span>
            ))}
          </div>
        </div>
        {hasOutro && (
          <div
            ref={slide}
            className="absolute inset-3 z-20 will-change-transform sm:inset-5"
            style={{ transform: "translate3d(0, 104%, 0)" }}
          >
            {outro}
          </div>
        )}
      </div>
    </section>
  );
}
